/**
 * @fileoverview `<OtpInput>` — saisie OTP à chiffres séparés.
 *
 * Composant contrôlé pour les codes SMS et MFA GNPC. Il expose une valeur
 * canonique composée uniquement de chiffres, supporte le collage complet
 * (`123456`), l'avance automatique du focus et le retour arrière ergonomique.
 *
 * @see docs/design-system-catalog.md — Forms / OtpInput
 */

import { useId, useMemo, useRef } from 'react';
import { cn } from '../lib/utils.ts';

type OtpInputSize = 'sm' | 'md' | 'lg';

export interface OtpInputProps {
  /** Valeur canonique, chiffres uniquement. Toute autre entrée est ignorée. */
  value?: string;
  /** Appelé avec la valeur canonique tronquée à `length`. */
  onChange?: (value: string) => void;
  /** Appelé quand toutes les cases sont remplies. */
  onComplete?: (value: string) => void;
  /** Nombre de chiffres. Défaut : 6, conforme OTP SMS et TOTP. */
  length?: number;
  /** Libellé accessible du groupe. Défaut : `Code de vérification`. */
  ariaLabel?: string;
  /** Nom du champ caché, utile pour formulaires HTML simples. */
  name?: string;
  /** Id de base pour les inputs. */
  id?: string;
  /** ID d'aide ou d'erreur relié aux inputs. */
  'aria-describedby'?: string;
  /** Marque le groupe en erreur. */
  'aria-invalid'?: boolean | 'true' | 'false';
  /** Active `autocomplete="one-time-code"` sur la première case. */
  autoComplete?: 'one-time-code' | 'off';
  size?: OtpInputSize;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

const SIZES: Record<OtpInputSize, string> = {
  sm: 'size-9 text-base',
  md: 'size-11 text-lg',
  lg: 'size-12 text-xl',
};

function onlyDigits(value: string, maxLength: number): string {
  return value.replace(/\D+/g, '').slice(0, maxLength);
}

/**
 * Saisie OTP segmentée.
 *
 * @param props - Props de contrôle du code OTP, dont `value`, `onChange`,
 * `onComplete`, `length`, taille visuelle et attributs ARIA.
 * @returns Groupe de champs numériques segmentés, avec input caché optionnel
 * quand `name` est fourni.
 *
 * @example
 * ```tsx
 * const [otp, setOtp] = useState('');
 * <OtpInput value={otp} onChange={setOtp} onComplete={submitOtp} />
 * ```
 */
export function OtpInput({
  value = '',
  onChange,
  onComplete,
  length = 6,
  ariaLabel = 'Code de vérification',
  name,
  id,
  autoComplete = 'one-time-code',
  size = 'md',
  disabled = false,
  readOnly = false,
  className,
  ...ariaProps
}: OtpInputProps): React.JSX.Element {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const normalized = onlyDigits(value, length);
  const chars = useMemo(
    () => Array.from({ length }, (_, index) => normalized[index] ?? ''),
    [length, normalized],
  );
  const invalid = ariaProps['aria-invalid'] === true || ariaProps['aria-invalid'] === 'true';

  const commit = (nextChars: string[]) => {
    const next = onlyDigits(nextChars.join(''), length);
    onChange?.(next);
    if (next.length === length) {
      onComplete?.(next);
    }
  };

  const focusIndex = (index: number) => {
    inputRefs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const updateFrom = (index: number, rawValue: string) => {
    const digits = onlyDigits(rawValue, length - index);
    const next = [...chars];

    if (digits.length === 0) {
      next[index] = '';
      commit(next);
      return;
    }

    digits.split('').forEach((digit, offset) => {
      next[index + offset] = digit;
    });

    commit(next);
    focusIndex(index + digits.length);
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      aria-invalid={ariaProps['aria-invalid']}
      aria-describedby={ariaProps['aria-describedby']}
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {name && <input type="hidden" name={name} value={normalized} />}
      {chars.map((char, index) => (
        <input
          key={`${baseId}-${index}`}
          ref={(node) => {
            inputRefs.current[index] = node;
          }}
          id={`${baseId}-${index}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? autoComplete : 'off'}
          value={char}
          disabled={disabled}
          readOnly={readOnly}
          aria-label={`${ariaLabel} — chiffre ${index + 1} sur ${length}`}
          aria-invalid={ariaProps['aria-invalid']}
          aria-describedby={ariaProps['aria-describedby']}
          className={cn(
            'border-input bg-background text-quipux-navy-950 rounded-md border text-center font-mono font-semibold tabular-nums outline-none',
            'transition-[color,box-shadow,border-color]',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
            invalid &&
              'border-danger ring-danger/20 focus-visible:border-danger focus-visible:ring-danger/20',
            SIZES[size],
          )}
          onChange={(event) => updateFrom(index, event.target.value)}
          onPaste={(event) => {
            event.preventDefault();
            updateFrom(index, event.clipboardData.getData('text'));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && !chars[index] && index > 0) {
              const next = [...chars];
              next[index - 1] = '';
              commit(next);
              focusIndex(index - 1);
            }
            if (event.key === 'ArrowLeft' && index > 0) {
              event.preventDefault();
              focusIndex(index - 1);
            }
            if (event.key === 'ArrowRight' && index < length - 1) {
              event.preventDefault();
              focusIndex(index + 1);
            }
          }}
        />
      ))}
    </div>
  );
}
